import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Upload } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';

const weekDays = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 0, label: 'Dim' }
];

const AnalystSettings = () => {
  const { user, token, fetchUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    phone: '',
    phone_pro: '',
    whatsapp: '',
    profile_photo_url: '',
    email: '',
    availability_status: true,
    availability_days: [1, 2, 3, 4, 5],
    availability_start: '09:00',
    availability_end: '18:00'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        job_title: user.job_title || '',
        phone: user.phone || '',
        phone_pro: user.phone_pro || '',
        whatsapp: user.whatsapp || '',
        profile_photo_url: user.profile_photo_url || '',
        email: user.email || '',
        availability_status: user.availability_status !== undefined ? user.availability_status : true,
        availability_days: Array.isArray(user.availability_days) ? user.availability_days : [1, 2, 3, 4, 5],
        availability_start: user.availability_start || '09:00',
        availability_end: user.availability_end || '18:00'
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      if (!user?.id) return;
      setSaving(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh user data in auth context
      await fetchUser(token);
      toast.success('Carte de visite mise à jour');
    } catch (error) {
      console.error('Error updating analyst card:', error);
      toast.error('Impossible de mettre à jour la carte');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (dayValue) => {
    setFormData((prev) => {
      const days = Array.isArray(prev.availability_days) ? prev.availability_days : [];
      const exists = days.includes(dayValue);
      return {
        ...prev,
        availability_days: exists ? days.filter((d) => d !== dayValue) : [...days, dayValue]
      };
    });
  };

  const availabilityDaysLabel = weekDays
    .filter((day) => formData.availability_days?.includes(day.value))
    .map((day) => day.label)
    .join(', ');
  const availabilityTimeLabel = formData.availability_start && formData.availability_end
    ? `${formData.availability_start} - ${formData.availability_end}`
    : 'Horaires non définis';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analyst-settings-page">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-white">Ma carte de visite</h1>
          <p className="text-sm text-muted-foreground">Ces informations s’affichent sur le dashboard du client.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Informations analyste</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Poste / Rôle</Label>
              <Input
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="bg-card/50"
                data-testid="analyst-card-job-title"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone pro</Label>
                <Input
                  value={formData.phone_pro}
                  onChange={(e) => setFormData({ ...formData, phone_pro: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-phone-pro"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-whatsapp"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-card/50"
                  data-testid="analyst-card-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Photo</Label>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <Input
                    value={formData.profile_photo_url}
                    onChange={(e) => setFormData({ ...formData, profile_photo_url: e.target.value })}
                    placeholder="URL de la photo..."
                    className="bg-card/50"
                    data-testid="analyst-card-photo-url"
                  />
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                    <Upload className="w-4 h-4" />
                    Uploader une photo
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload-image`, fd, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                        setFormData(prev => ({ ...prev, profile_photo_url: fullUrl }));
                        toast.success('Photo uploadee');
                      } catch { toast.error('Erreur upload'); }
                    }} />
                  </label>
                </div>
                {formData.profile_photo_url && (
                  <img src={formData.profile_photo_url} alt="Preview" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}
              </div>
            </div>

            <div className="rounded-lg border border-slate-700/50 bg-slate-900/30 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Disponibilité</p>
                  <p className="text-xs text-muted-foreground">Activez/désactivez votre présence client.</p>
                </div>
                <Switch
                  checked={formData.availability_status}
                  onCheckedChange={(value) => setFormData({ ...formData, availability_status: value })}
                  data-testid="analyst-availability-toggle"
                />
              </div>

              <div className="space-y-2">
                <Label>Jours disponibles</Label>
                <div className="flex flex-wrap gap-3">
                  {weekDays.map((day) => (
                    <label key={day.value} className="flex items-center gap-2 text-sm text-gray-300">
                      <Checkbox
                        checked={formData.availability_days?.includes(day.value)}
                        onCheckedChange={() => toggleDay(day.value)}
                        data-testid={`availability-day-${day.value}`}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Heure de début</Label>
                  <Input
                    type="time"
                    value={formData.availability_start}
                    onChange={(e) => setFormData({ ...formData, availability_start: e.target.value })}
                    className="bg-card/50"
                    data-testid="availability-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure de fin</Label>
                  <Input
                    type="time"
                    value={formData.availability_end}
                    onChange={(e) => setFormData({ ...formData, availability_end: e.target.value })}
                    className="bg-card/50"
                    data-testid="availability-end-time"
                  />
                </div>
              </div>
            </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-gold"
                data-testid="analyst-card-save-button"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer la carte'}
              </Button>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader>
              <CardTitle>Aperçu client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-800 overflow-hidden">
                  {formData.profile_photo_url ? (
                    <img src={formData.profile_photo_url} alt="Analyst" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      {formData.first_name?.[0] || 'A'}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-white font-semibold">{formData.first_name} {formData.last_name}</p>
                  <p className="text-sm text-muted-foreground">{formData.job_title || 'Analyste'}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-300">
                {formData.phone && <p>Téléphone: {formData.phone}</p>}
                {formData.phone_pro && <p>Téléphone pro: {formData.phone_pro}</p>}
                {formData.whatsapp && <p>WhatsApp: {formData.whatsapp}</p>}
                {formData.email && <p>Email: {formData.email}</p>}
              </div>
              <div className="pt-2 text-sm text-gray-300 space-y-1">
                <p>
                  Disponibilité:{' '}
                  <span className={formData.availability_status ? 'text-emerald-400' : 'text-rose-400'}>
                    {formData.availability_status ? 'Disponible' : 'Indisponible'}
                  </span>
                </p>
                <p>Jours: {availabilityDaysLabel || 'Non définis'}</p>
                <p>Heures: {availabilityTimeLabel}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalystSettings;